import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import useSocialMeta from "../hooks/useSocialMeta";
import "./LandingPage.css";
import TrustSignalStrip from "../components/TrustSignalStrip";

const HERO_BG = "/brand/church-interior.jpg";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (c) => `$${((c || 0) / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const PLANS = [
  {
    tag: "Pay Per Report",
    name: "Single Report",
    price: "$29",
    cadence: "",
    plan: "single_report",
    cta: "Get Started",
    desc: "One complete SundayBuilder™ worship plan — perfect for special liturgies, guest planning, or occasional needs.",
    features: [
      "One complete SundayBuilder™ package",
      "PDF export & professional formatting",
      "One-time purchase — yours to keep",
    ],
    featured: false,
  },
  {
    tag: "Most Popular",
    name: "Parish Pro",
    price: "$49",
    cadence: "/mo",
    plan: "parish_pro",
    cta: "Subscribe",
    desc: "Your parish's permanent worship planning system — the intelligence learns your parish over time, so every season is easier and every year builds on the last.",
    features: [
      "Unlimited SundayBuilder™ planning",
      "Parish Profile & AI Parish Memory",
      "Seasonal, Holy Week & Annual planning",
      "Report History & all future Liturgical Intelligence™ modules",
    ],
    featured: true,
  },
  {
    tag: "For Large Ministries",
    name: "Cathedral Edition™",
    price: "$299",
    cadence: "/mo",
    plan: "cathedral",
    cta: "Subscribe",
    desc: "Premium tier for cathedrals, collegiate chapels, shrines, and complex ministries.",
    features: [
      "Everything in Parish Pro",
      "Priority Ordo AI™ processing",
      "Designed for larger parishes & teams",
    ],
    featured: false,
  },
];

const PROBLEMS = [
  {
    num: "i.",
    title: "Planning Fatigue",
    body: "Clergy and musicians carry weekly planning pressure while trying to preserve theological depth and pastoral care.",
  },
  {
    num: "ii.",
    title: "Scattered Materials",
    body: "Bulletins, music plans, prayers, and notes are assembled by hand instead of shaped from one coherent liturgical vision.",
  },
  {
    num: "iii.",
    title: "Generic Tools Fall Short",
    body: "Churches don't need random religious content. They need planning shaped by tradition, context, and real parish life.",
  },
];

const DELIVERABLES = [
  "Orders of Service",
  "Music Plans",
  "Hymn Suggestions",
  "Bulletin Copy",
  "Choir Notes",
  "Clergy Notes",
  "Organ Voluntaries",
  "Prayers of the People",
];

const PLATFORM = [
  // ✅ Available
  { name: "SundayBuilder™", desc: "Complete, ministry-ready worship plans for any Sunday or season.", status: "Available" },
  { name: "Musical Intelligence™", desc: "The world's first explainable liturgical planning engine. Verified. Deterministic. Built for the Church.", status: "Available" },
  { name: "Parish Intelligence™", desc: "Reads your denomination, context, and worship tradition into every plan.", status: "Available" },
  { name: "Parish Profiles™", desc: "Traditions, hymnals, ensembles, and local customs captured in structured form.", status: "Available" },
  { name: "Parish Memory™", desc: "Learns your parish over time, so every season builds on the last.", status: "Available" },
  { name: "Explainable Recommendations", desc: "Every hymn ships with a why — scripture, season, tradition, rights, and score.", status: "Available" },
  // ⭐ First-class products
  { name: "Annual Planner™", desc: "Prepare an entire year of worship from one planning workspace.", status: "Available" },
  // 🚧 In Development
  { name: "Denominational Catalog Expansion", desc: "Verified Roman Catholic, Lutheran, Methodist, and Presbyterian hymnals.", status: "In Development" },
  { name: "Annual Planner 2.0", desc: "Choir workload, volunteer schedules, and drag-and-drop planning.", status: "In Development" },
  { name: "Choir Scheduling", desc: "Rehearsal calendar and anthem load balancing across the year.", status: "In Development" },
  { name: "Volunteer Planning", desc: "Lector, acolyte, and altar-guild scheduling aligned to the liturgical year.", status: "In Development" },
  { name: "Customer Recommendation Insights", desc: "In-report tier badges, alternatives, and reasoning for every hymn.", status: "In Development" },
  // 🕒 Planned
  { name: "Formation Planner™", desc: "Formation and catechesis aligned to the rhythm of the liturgical year.", status: "Planned" },
  { name: "Analytics™", desc: "Insight into planning, participation, and the ministry year.", status: "Planned" },
  { name: "Diocesan Intelligence™", desc: "Coordinated worship planning intelligence across many parishes.", status: "Planned" },
];

export default function LandingPage() {
  useSocialMeta({
    title: "SundayBuilder™ — The planning companion of the Church",
    description: "The planning companion of the Church. Prepare every Sunday with confidence.",
    path: "/",
  });
  const navigate = useNavigate();
  const [fc, setFc] = useState(null);
  useEffect(() => { axios.get(`${API}/founding-circle`).then((r) => setFc(r.data)).catch(() => {}); }, []);

  const goToPlan = (plan) => {
    // Auth-first funnel: pricing → register → authenticated $49 checkout in /account.
    navigate(`/register?plan=${plan}`);
  };

  useEffect(() => {
    const els = document.querySelectorAll(".li-reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="li-page">
      <nav className="li-nav">
        <div className="li-wrap">
          <a href="#top" className="li-brand" data-testid="brand-logo" aria-label="Liturgical Intelligence — powered by Ordo AI">
            <img
              src="/brand/li-logo-sm.png"
              alt="Liturgical Intelligence™ — Powered by Ordo AI™"
              className="li-brand-logo"
              data-testid="brand-logo-img"
            />
            <span className="li-brand-wordmark">Liturgical<span>Intelligence</span>™</span>
          </a>
          <div className="li-nav-links">
            <a href="#li-suite" data-testid="nav-link-platform">Platform</a>
            <a href="#li-philosophy" data-testid="nav-link-philosophy">Philosophy</a>
            <Link to="/login" data-testid="nav-link-signin">Sign In</Link>
            <Link to="/the-ask" className="li-nav-cta" data-testid="nav-cta-founding-circle">Founding Circle</Link>
          </div>
        </div>
      </nav>

      <section className="li-hero" id="top">
        <div className="li-hero-bg" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div className="li-hero-grain" aria-hidden="true" />
        <div className="li-wrap li-hero-inner">
          <div className="li-hero-masthead li-reveal" data-testid="hero-masthead">
            <span className="li-hero-masthead-rule" aria-hidden="true" />
            <span className="li-hero-masthead-line" data-testid="hero-platform-line">
              Liturgical Intelligence<sup>™</sup>
            </span>
            <span className="li-hero-masthead-sub" data-testid="hero-engine-line">
              Powered by Ordo AI<sup>™</sup>
            </span>
            <span className="li-hero-masthead-rule" aria-hidden="true" />
          </div>

          <h1 className="li-reveal li-hero-headline" data-testid="hero-headline">
            Worship Planning Rooted in <em>Liturgical Tradition.</em>
          </h1>

          <p className="li-reveal li-hero-supporting" data-testid="hero-supporting-copy">
            SundayBuilder<sup>™</sup> helps clergy, musicians, and ministry leaders prepare
            worship that is faithful, beautiful, practical, and grounded in the life of a
            real congregation.
          </p>

          <div className="li-hero-actions li-reveal">
            <Link className="li-button" to="/register" data-testid="hero-cta-primary">Begin the Experience</Link>
            <a className="li-button dark" href="#li-suite" data-testid="hero-cta-secondary">Read the Colophon</a>
          </div>
        </div>
      </section>

      {/* Product Suite Reveal — publisher-house imprint layout */}
      <section className="li-section li-suite" id="li-suite" data-testid="product-suite-reveal">
        <div className="li-wrap">
          <div className="li-eyebrow li-reveal" data-testid="suite-eyebrow">The Imprint</div>
          <h2 className="li-reveal li-suite-title">A press, a workshop, and the engine that binds them.</h2>
          <div className="li-suite-triptych">
            <article className="li-suite-panel li-reveal" data-testid="suite-panel-platform">
              <div className="li-suite-numeral">I</div>
              <div className="li-suite-tag">Platform</div>
              <h3 className="li-suite-name">Liturgical Intelligence<sup>™</sup></h3>
              <p className="li-suite-body">
                The house imprint. A sovereign infrastructure for the timeless Church — its
                catalogs, its memory, its editorial standards. Every product we publish sits
                under this masthead.
              </p>
            </article>
            <article className="li-suite-panel li-reveal li-suite-panel--flagship" data-testid="suite-panel-product">
              <div className="li-suite-numeral">II</div>
              <div className="li-suite-tag">Flagship Product</div>
              <h3 className="li-suite-name">SundayBuilder<sup>™</sup></h3>
              <p className="li-suite-body">
                The premier workspace for divine worship planning. Where clergy, musicians, and
                ministry leaders assemble a Sunday liturgy the way an editor prepares a folio
                — with restraint, provenance, and a sense of occasion.
              </p>
            </article>
            <article className="li-suite-panel li-reveal" data-testid="suite-panel-engine">
              <div className="li-suite-numeral">III</div>
              <div className="li-suite-tag">Engine</div>
              <h3 className="li-suite-name">Ordo AI<sup>™</sup></h3>
              <p className="li-suite-body">
                The deterministic computational engine that binds them. Canonical, auditable,
                and quiet — Ordo AI runs the press. It does not speak on its own.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="li-trust-signal-section" data-testid="landing-trust-signal">
        <div className="li-wrap" style={{ maxWidth: 960 }}>
          <TrustSignalStrip />
        </div>
      </section>

      {fc && (
        <section className="li-fc-banner" data-testid="landing-founding-strip">
          <div className="li-wrap li-fc-banner-inner">
            <div className="li-fc-banner-main">
              <div className="li-fc-banner-eyebrow">The Founding Circle · Launch Progress</div>
              <div className="li-fc-banner-stats">
                <div className="li-fc-stat">
                  <span className="li-fc-stat-num" data-testid="landing-fc-raised">{usd(fc.raised_cents)}</span>
                  <span className="li-fc-stat-label">Raised</span>
                </div>
                <div className="li-fc-stat">
                  <span className="li-fc-stat-num">{usd(fc.goal_cents)}</span>
                  <span className="li-fc-stat-label">Goal</span>
                </div>
                <div className="li-fc-stat">
                  <span className="li-fc-stat-num">{fc.supporters_count}</span>
                  <span className="li-fc-stat-label">Founding Supporter{fc.supporters_count === 1 ? "" : "s"}</span>
                </div>
              </div>
              <div className="li-fc-banner-bar"><div style={{ width: `${fc.percent}%` }} /></div>
            </div>
            <Link to="/the-ask" className="li-fc-banner-cta" data-testid="landing-fc-cta">Become a Founding Supporter</Link>
          </div>
        </section>
      )}

      <section className="li-section li-white" id="li-problem">
        <div className="li-wrap">
          <div className="li-eyebrow li-reveal">The Problem</div>
          <h2 className="li-reveal">
            Churches spend hours every week assembling worship from disconnected sources.
          </h2>
          <p className="li-reveal">
            Orders of Service, music selections, bulletin copy, Prayers of the People, choir notes,
            clergy planning materials, and seasonal worship resources too often live in separate
            documents, separate inboxes, and separate conversations.
          </p>
          <div className="li-grid">
            {PROBLEMS.map((p) => (
              <div className="li-card li-reveal" key={p.title}>
                <div className="li-num">{p.num}</div>
                <h3>{p.title}</h3>
                <p>{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="li-section li-foreword" id="li-philosophy" data-testid="editorial-foreword-section">
        <div className="li-wrap">
          <div className="li-foreword-card">
            <div className="li-foreword-header">
              <span className="li-foreword-eyebrow">Foreword</span>
              <span className="li-foreword-folio">Volume I · MMXXVI</span>
            </div>
            <p className="li-foreword-body">
              <span className="li-foreword-dropcap">F</span>or more than twenty years I have served
              Episcopal and Roman Catholic congregations as a church musician, liturgical planner,
              and ministry leader. Liturgical Intelligence<sup>™</sup> did not begin as a start-up.
              It began in a choir loft, in a sacristy, in a rehearsal room in the last hour before
              a Sunday — where the work of preparation is quiet, exacting, and ancient.
            </p>
            <p className="li-foreword-body">
              What we are publishing here is not software so much as an editorial workshop:
              a way for parishes to compose, remember, and prepare their worship with the
              confidence of a serious press. SundayBuilder<sup>™</sup> is our first title.
              Ordo AI<sup>™</sup> is the engine that binds it. Both sit under the imprint of
              Liturgical Intelligence<sup>™</sup> — a house we intend to build, one careful
              volume at a time.
            </p>
            <div className="li-foreword-signoff">
              <span className="li-foreword-signature">Rasaan Hakiem</span>
              <span className="li-foreword-title">Founder &amp; Editor</span>
            </div>
          </div>
        </div>
      </section>

      <section className="li-section li-founder" id="li-founder">
        <div className="li-wrap">
          <div className="li-eyebrow li-reveal">The Founder</div>
          <h2 className="li-reveal">Built by someone who has served the Church.</h2>
          <p className="li-reveal">
            For more than twenty years, Rasaan Hakiem has served Episcopal and Roman Catholic
            congregations as a church musician, liturgical planner, and ministry leader.
          </p>
          <p className="li-reveal">
            Liturgical Intelligence™ grew directly out of those years of ministry. It wasn't built as
            a generic AI platform — it was shaped by choir lofts, sacristies, rehearsal rooms, parish
            offices, and Sunday mornings.
          </p>
        </div>
      </section>

      <section className="li-section li-principle" id="li-principle">
        <div className="li-wrap">
          <div className="li-eyebrow li-reveal">The Liturgical Intelligence™ Principle</div>
          <div className="quote li-reveal">
            The sacred belongs to a people, a place, a moment, and a liturgy.
          </div>
          <p className="li-reveal" style={{ margin: "0 auto" }}>
            SundayBuilder™ is not designed to generate the theoretically "best" worship plan in
            isolation. It is designed to generate the most faithful plan for a particular
            congregation, in a particular place, at a particular moment in its life together.
          </p>
        </div>
      </section>

      <section className="li-section li-solution" id="li-solution">
        <div className="li-wrap">
          <div className="li-eyebrow li-reveal">The Solution</div>
          <h2 className="li-reveal">SundayBuilder™</h2>
          <div className="li-tagline li-reveal">
            Worship planning rooted in liturgical tradition, pastoral context, and sacred music
            leadership.
          </div>
          <p className="li-reveal">
            SundayBuilder™ receives the essential details of a service — denomination, date, worship
            tradition, available musicians, parish context, and requested deliverables — then
            generates a structured, ministry-ready planning package. Built for Roman Catholic,
            Episcopal, Anglican, Lutheran, Methodist, Presbyterian, and other liturgical traditions.
          </p>
          <div className="li-deliverables">
            {DELIVERABLES.map((d) => (
              <div className="li-reveal" key={d}>{d}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="li-section li-musical-intelligence" id="li-musical-intelligence" data-testid="landing-musical-intelligence">
        <div className="li-wrap">
          <div className="li-eyebrow li-reveal">Musical Intelligence™</div>
          <h2 className="li-reveal">The world's first explainable liturgical planning engine.</h2>
          <div className="li-mi-tagline li-reveal">
            <span>Verified.</span>
            <span>Deterministic.</span>
            <span>Built for the Church.</span>
          </div>
          <p className="li-mi-lead li-reveal">
            Unlike generic AI assistants, Liturgical Intelligence™ produces deterministic,
            explainable recommendations grounded in liturgical tradition, verified hymn resources,
            and the appointed readings of the Church.
          </p>
          <p className="li-reveal">
            Every hymn recommendation carries verified canonical identity, documented rights,
            structured scripture and theme metadata, tradition-aware rules, and a transparent
            score breakdown. No guesswork. No hallucinated hymn numbers. No LLM improvisation.
          </p>
          <div className="li-mi-facts">
            <div className="li-reveal"><strong>7</strong> Traditions Supported</div>
            <div className="li-reveal"><strong>145+</strong> Canonical Works</div>
            <div className="li-reveal"><strong>Every</strong> Recommendation Explained</div>
            <div className="li-reveal"><strong>Deterministic</strong> — Never Black Box</div>
          </div>
          <div className="li-mi-cta li-reveal">
            <Link to="/musical-intelligence" className="li-mi-cta-link"
                  data-testid="landing-mi-cta">
              See how Musical Intelligence™ works&nbsp;→
            </Link>
          </div>
        </div>
      </section>

      <section className="li-section li-ecosystem" id="li-ecosystem">
        <div className="li-wrap">
          <div className="li-eyebrow li-reveal">The Liturgical Intelligence™ Platform</div>
          <h2 className="li-reveal">One ecosystem of ministry applications — all powered by the Ordo AI™ Intelligence Engine.</h2>
          <p className="li-reveal">
            Liturgical Intelligence™ is not a single tool. It is a growing platform of applications,
            each ordered by the same engine — Ordo AI™ — to serve the full life of a parish.
          </p>
          <p className="li-reveal li-platform-why">
            Every application shares the same Parish Profile™, Parish Memory™, and Ordo AI™ Intelligence
            Engine, allowing every recommendation to become more personalized over time.
          </p>

          <div className="li-platform-grid">
            {PLATFORM.map((app, i) => (
              <div className="li-platform-card li-reveal" key={app.name} data-testid={`platform-app-${i}`}>
                <div className="li-platform-head">
                  <h3>{app.name}</h3>
                  <span className={`li-platform-badge li-platform-${(app.status || "").toLowerCase().replace(/\s+/g,"-")}`}>{app.status}</span>
                </div>
                <p>{app.desc}</p>
                <span className="li-platform-engine">Powered by the Ordo AI™ Intelligence Engine</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="li-season" data-testid="landing-season">
        <div className="li-wrap">
          <div className="li-eyebrow li-reveal">Planning Season</div>
          <h2 className="li-reveal">Churches are planning their ministry year right now.</h2>
          <p className="li-reveal">
            The upcoming season is taking shape in parishes everywhere. Liturgical Intelligence™ helps
            clergy and musicians plan with confidence — and finish before September.
          </p>
          <Link className="li-button li-reveal" to="/register" data-testid="season-cta-btn">Start Your Free Experience</Link>
        </div>
      </section>

      <section className="li-section li-pricing" id="li-pricing">
        <div className="li-wrap">
          <div className="li-eyebrow li-reveal">Access SundayBuilder™</div>
          <h2 className="li-reveal">Choose the right plan for your ministry.</h2>
          <p className="li-reveal">
            Access begins with purchase or subscription. After payment, you'll be directed to the
            secure SundayBuilder™ planning intake — available only to paying customers.
          </p>

          <div className="li-price-grid">
            {PLANS.map((plan) => (
              <div
                className={`li-price-card li-reveal${plan.featured ? " featured" : ""}`}
                key={plan.plan}
                data-testid={`plan-card-${plan.plan}`}
              >
                <div className="li-price-tag">{plan.tag}</div>
                <h3>{plan.name}</h3>
                <div className="li-price">
                  {plan.price}
                  {plan.cadence && <small>{plan.cadence}</small>}
                </div>
                <div className="desc">{plan.desc}</div>
                <ul className="li-feat">
                  {plan.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                <button
                  className="li-button li-plan-btn"
                  onClick={() => goToPlan(plan.plan)}
                  data-testid={`checkout-${plan.plan}`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>

          <div className="li-manage">
            Already subscribed?{" "}
            <a
              href="https://billing.stripe.com/p/login/9B63cu2Ia1048Yaf5u3Je00"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="manage-subscription-link"
            >
              Manage Subscription
            </a>
            {"  ·  "}
            <Link to="/login" data-testid="access-builder-link">
              Already a member? Enter your email
            </Link>
          </div>
        </div>
      </section>

      <section className="li-cta">
        <h2>Purchase access before opening SundayBuilder™.</h2>
        <p>
          Choose one-time access, Parish Edition™, or Cathedral Edition™. After payment, you'll be
          directed straight to the SundayBuilder™ intake form.
        </p>
        <a className="li-button dark" href="#li-pricing" data-testid="cta-choose-btn" style={{ borderColor: "#111E2B", color: "#111E2B" }}>
          Choose Access Level
        </a>
      </section>

      <footer className="li-footer">
        <div className="li-wrap">
          <img src="/brand/rasaan-hakiem-logo.png" alt="Rasaan Hakiem — Sacred Music & Liturgical Intelligence" className="li-footer-logo" />
          <div className="li-footer-text">
            Liturgical<span>Intelligence</span>™ · SundayBuilder™ — Faithful. Practical. Beautiful.
            Grounded.
          </div>
        </div>
      </footer>
    </div>
  );
}
